// src/features/admin/pages/AdminToxEditPage.tsx
import React, { useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { api } from "@/lib/api";
import { notify } from "@/ui/notify";
import { useNavigate, useParams } from "react-router-dom";
import "../admin.css";
import { BORDER } from "@/ui/tokens";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");
function resolveImageUrl(raw?: string | null): string | undefined {
    if (!raw) return undefined;
    if (raw.startsWith("/") && API_BASE) return `${API_BASE}${raw}`;
    return raw;
}

/* ── 인라인 font-size 등록 (module scope, 최초 1회) ── */
try {
    const SizeStyle = Quill.import("attributors/style/size") as any;
    SizeStyle.whitelist = ["0.8em", "1.3em", "1.7em"];
    Quill.register(SizeStyle, true);
} catch {}

const TOOLBAR = [
    ["bold", "italic", "underline"],
    [{ size: ["0.8em", false, "1.3em", "1.7em"] }],
    [{ color: ["#111111", "#e53e3e", "#2563eb", "#16a34a", "#9ca3af"] }],
    ["clean"],
];

/* ── 사이즈 드롭다운 한글 레이블 ── */
const QUILL_STYLE = `
.ql-editor { min-height: 280px; font-size: 16px; line-height: 1.7; font-family: inherit; }
.ql-toolbar.ql-snow  { border-radius: 8px 8px 0 0; border-color: #111; }
.ql-container.ql-snow{ border-radius: 0 0 8px 8px; border-color: #111; border-top: none; }
.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before            { content: '보통'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="0.8em"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="0.8em"]::before { content: '작게'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="1.3em"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="1.3em"]::before { content: '크게'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="1.7em"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="1.7em"]::before { content: '아주 크게'; }
`;

export default function AdminToxEditPage() {
    const { id } = useParams();
    const isNew = !id || id === "new";

    const [title, setTitle]               = useState("");
    const [imageUrl, setImageUrl]         = useState<string | null>(null);
    const [bodyImageUrl, setBodyImageUrl] = useState<string | null>(null);
    const [status, setStatus]             = useState<"DRAFT"|"PUBLISHED"|"ARCHIVED">("DRAFT");
    const [loading, setLoading]           = useState(false);
    const [editorEmpty, setEditorEmpty]   = useState(true);

    const editorDivRef = useRef<HTMLDivElement>(null);
    const quillRef     = useRef<Quill | null>(null);
    const nav          = useNavigate();

    /* Quill 초기화 */
    useEffect(() => {
        const container = editorDivRef.current;
        if (!container) return;
        if (container.classList.contains('ql-container')) return;

        const q = new Quill(container, {
            theme: "snow",
            modules: { toolbar: TOOLBAR },
            placeholder: "본문을 입력하세요...",
        });
        q.on("text-change", () => setEditorEmpty(q.getLength() <= 1));
        quillRef.current = q;

        return () => {
            quillRef.current = null;
            const prev = container.previousElementSibling;
            if (prev?.classList.contains('ql-toolbar')) prev.remove();
            container.className = '';
            container.innerHTML = '';
        };
    }, []);

    /* 기존 글 로드 */
    useEffect(() => {
        if (isNew || !id) return;
        (async () => {
            try {
                const data = await api(`/admin/tox/${id}`);
                const t = data?.tox;
                if (!t) return;
                setTitle(t.title || "");
                setImageUrl(t.image_url || null);
                setBodyImageUrl(t.body_image_url || null);
                setStatus((t.status as any) || "DRAFT");
                const content = t.content || "";
                if (content && quillRef.current) {
                    quillRef.current.clipboard.dangerouslyPasteHTML(content);
                    setEditorEmpty(quillRef.current.getLength() <= 1);
                }
            } catch {}
        })();
    }, [isNew, id]);

    const uploadFile = async (file: File): Promise<string> => {
        const fd = new FormData();
        fd.append("image", file);
        const data = await api("/admin/tox/upload", { method: "POST", formData: fd });
        return data.url;
    };

    const onSave = async () => {
        const htmlContent = quillRef.current?.root.innerHTML || "";
        setLoading(true);
        try {
            if (isNew) {
                const data = await api("/admin/tox", {
                    method: "POST",
                    json: { title, content: htmlContent, image_url: imageUrl || undefined, body_image_url: bodyImageUrl || undefined },
                });
                const newId = data?.tox?.id;
                if (newId) nav(`/admin/tox/${newId}`, { replace: true });
            } else {
                await api(`/admin/tox/${id}`, {
                    method: "PATCH",
                    json: { title, content: htmlContent, image_url: imageUrl || null, body_image_url: bodyImageUrl || null, status },
                });
                notify("저장했습니다.");
            }
        } finally { setLoading(false); }
    };

    return (
        <div className="adm-page">
            <style>{QUILL_STYLE}</style>
            <h2>{isNew ? "새 글" : `편집 #${id}`}</h2>
            <div style={{ display: "grid", gap: 12 }}>

                <label>제목</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                    className="adm-input" style={{ height: 40 }} />

                <label>본문</label>
                <div ref={editorDivRef} />

                <label>대표 이미지 — 썸네일 (필수)</label>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <input type="file" accept="image/*" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (f) setImageUrl(await uploadFile(f));
                    }} />
                    {imageUrl && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <img src={resolveImageUrl(imageUrl)} alt="" style={{ height: 80, border: BORDER.muted }} />
                            <button type="button" onClick={() => setImageUrl(null)} className="adm-btn-danger" style={{ border: "none" }}>삭제</button>
                        </div>
                    )}
                </div>

                <label>본문 이미지 (선택) — 상세 페이지에 표시</label>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <input type="file" accept="image/*" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (f) setBodyImageUrl(await uploadFile(f));
                    }} />
                    {bodyImageUrl && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <img src={resolveImageUrl(bodyImageUrl)} alt="" style={{ height: 80, border: BORDER.muted }} />
                            <button type="button" onClick={() => setBodyImageUrl(null)} className="adm-btn-danger" style={{ border: "none" }}>삭제</button>
                        </div>
                    )}
                </div>

                {!isNew && (
                    <>
                        <label>상태</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value as any)}
                            className="adm-select-lg">
                            <option value="DRAFT">DRAFT</option>
                            <option value="PUBLISHED">PUBLISHED</option>
                            <option value="ARCHIVED">ARCHIVED</option>
                        </select>
                    </>
                )}

                <div>
                    <button onClick={onSave} disabled={!title || editorEmpty || loading}
                        className="adm-btn" style={{ height: 40, padding: "0 14px" }}>
                        {loading ? "저장 중…" : "저장"}
                    </button>
                </div>
            </div>
        </div>
    );
}
